import React, { useState, useRef, useEffect } from 'react';
import { useSupport } from './SupportContext';

const SupportWidget: React.FC = () => {
  const { isOpen, setIsOpen, messages, sendMessageToBackend, isTyping } = useSupport();
  const [inputMessage, setInputMessage] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputMessage.trim() || isTyping) return;
    const messageText = inputMessage;
    setInputMessage('');
    await sendMessageToBackend(messageText);
  };

  const formatTime = (date: Date) => {
    const d = new Date(date);
    return isNaN(d.getTime())
      ? new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <>
      {/* Icône Flottante Élégante (Ronde) */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="support-widget-button"
          style={{
            position: 'fixed',
            bottom: '24px',
            left: '24px',
            zIndex: 1000,
            width: '56px',
            height: '56px',
            borderRadius: '50%',
            backgroundColor: '#294b5a',
            border: 'none',
            color: 'white',
            cursor: 'pointer',
            boxShadow: '0 6px 20px rgba(0, 0, 0, 0.25)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.3s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#1a3641';
            e.currentTarget.style.transform = 'scale(1.08)';
            e.currentTarget.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.3)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = '#294b5a';
            e.currentTarget.style.transform = 'scale(1)';
            e.currentTarget.style.boxShadow = '0 6px 20px rgba(0, 0, 0, 0.25)';
          }}
          title="Chat with AI Support"
        >
          {/* Icône SVG Chat moderne et épurée */}
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
          </svg>
        </button>
      )}

      {/* Fenêtre de Chat Pop-up */}
      {isOpen && (
        <div
          className="support-chat-window"
          style={{
            position: 'fixed',
            bottom: '24px',
            left: '24px',
            zIndex: 1001,
            width: '380px',
            height: '520px',
            backgroundColor: 'white',
            borderRadius: '16px',
            boxShadow: '0 12px 40px rgba(0, 0, 0, 0.25)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            fontFamily: 'Arial, sans-serif'
          }}
        >
          {/* Header */}
          <div
            style={{
              backgroundColor: '#294b5a',
              color: 'white',
              padding: '16px 20px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
            }}
          >
            <div>
              <h3 style={{
                margin: 0,
                fontSize: '16px',
                fontWeight: '700',
                letterSpacing: '0.5px'
              }}>
                Silver Jarz AI Support
              </h3>
              <p style={{
                margin: '2px 0 0 0',
                fontSize: '11px',
                opacity: 0.8,
                fontWeight: '400'
              }}>
                Online | Ask me anything
              </p>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              style={{
                background: 'rgba(255, 255, 255, 0.1)',
                border: 'none',
                color: 'white',
                fontSize: '16px',
                cursor: 'pointer',
                borderRadius: '50%',
                width: '30px',
                height: '30px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
              }}
            >
              ×
            </button>
          </div>

          {/* Messages Container */}
          <div
            style={{
              flex: 1,
              padding: '16px',
              overflowY: 'auto',
              backgroundColor: '#f8fafc',
              backgroundImage: 'linear-gradient(45deg, #f8fafc 0%, #f1f5f9 100%)'
            }}
          >
            {messages.map((message) => (
              <div
                key={message.id}
                style={{
                  display: 'flex',
                  justifyContent: message.sender === 'user' ? 'flex-end' : 'flex-start',
                  marginBottom: '12px',
                  alignItems: 'flex-end',
                  gap: '8px'
                }}
              >
                {message.sender === 'support' && (
                  <div
                    style={{
                      width: '26px',
                      height: '26px',
                      borderRadius: '50%',
                      backgroundColor: '#294b5a',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '11px',
                      color: 'white',
                      fontWeight: 'bold',
                      flexShrink: 0
                    }}
                  >
                    AI
                  </div>
                )}
                <div
                  style={{
                    maxWidth: '75%',
                    padding: '10px 14px',
                    borderRadius: message.sender === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                    backgroundColor: message.sender === 'user' ? '#294b5a' : 'white',
                    color: message.sender === 'user' ? 'white' : '#374151',
                    fontSize: '13px',
                    lineHeight: '1.4',
                    wordWrap: 'break-word',
                    boxShadow: message.sender === 'support' ? '0 2px 6px rgba(0, 0, 0, 0.05)' : 'none',
                    border: message.sender === 'support' ? '1px solid #e5e7eb' : 'none',
                  }}
                >
                  <div style={{ marginBottom: '3px' }}>{message.text}</div>
                  <div
                    style={{
                      fontSize: '10px',
                      opacity: 0.6,
                      textAlign: message.sender === 'user' ? 'right' : 'left',
                      fontWeight: '500'
                    }}
                  >
                    {formatTime(message.timestamp)}
                  </div>
                </div>
                {message.sender === 'user' && (
                  <div
                    style={{
                      width: '26px',
                      height: '26px',
                      borderRadius: '50%',
                      backgroundColor: '#10b981',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '11px',
                      color: 'white',
                      fontWeight: 'bold',
                      flexShrink: 0
                    }}
                  >
                    You
                  </div>
                )}
              </div>
            ))}

            {isTyping && (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'flex-start',
                  marginBottom: '12px',
                  alignItems: 'flex-end',
                  gap: '8px'
                }}
              >
                <div
                  style={{
                    width: '26px',
                    height: '26px',
                    borderRadius: '50%',
                    backgroundColor: '#294b5a',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '11px',
                    color: 'white',
                    fontWeight: 'bold',
                    flexShrink: 0
                  }}
                >
                  AI
                </div>
                <div
                  style={{
                    padding: '10px 14px',
                    borderRadius: '16px 16px 16px 4px',
                    backgroundColor: 'white',
                    color: '#374151',
                    fontSize: '13px',
                    fontStyle: 'italic',
                    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.05)',
                    border: '1px solid #e5e7eb',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  <div className="typing-dots">
                    <span style={{ animationDelay: '0s' }}>●</span>
                    <span style={{ animationDelay: '0.2s' }}>●</span>
                    <span style={{ animationDelay: '0.4s' }}>●</span>
                  </div>
                  Thinking...
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <form
            onSubmit={handleSendMessage}
            style={{
              padding: '14px',
              borderTop: '1px solid #e5e7eb',
              backgroundColor: 'white'
            }}
          >
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <input
                type="text"
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                placeholder="Type your message..."
                disabled={isTyping}
                style={{
                  flex: 1,
                  padding: '10px 14px',
                  border: '1px solid #d1d5db',
                  borderRadius: '20px',
                  fontSize: '13px',
                  outline: 'none',
                  backgroundColor: '#f8fafc',
                  transition: 'all 0.2s ease',
                  fontFamily: 'inherit'
                }}
              />
              <button
                type="submit"
                disabled={!inputMessage.trim() || isTyping}
                style={{
                  padding: '10px 16px',
                  backgroundColor: '#294b5a',
                  color: 'white',
                  border: 'none',
                  borderRadius: '20px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: '600',
                  opacity: (inputMessage.trim() && !isTyping) ? 1 : 0.5,
                  transition: 'all 0.2s ease',
                }}
              >
                Send
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Styles pour l'animation des points */}
      <style>
        {`
          .typing-dots {
            display: flex;
            gap: 2px;
          }
          .typing-dots span {
            animation: typing-dot 1.4s infinite;
            font-size: 8px;
          }
          @keyframes typing-dot {
            0%, 60%, 100% {
              opacity: 0.3;
            }
            30% {
              opacity: 1;
            }
          }
        `}
      </style>
    </>
  );
};

export default SupportWidget;